import { useOktaAuth } from '@okta/okta-react';
import React, { useMemo } from 'react';
import applicationSettings from '../../../../Core/Settings/ApplicationSettings';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import NavigationRoutes from '../../../../Layouts/NavigationRoutes';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserName } from '../../../../Redux/Slices/UserSlice';
import helpIcon from '../../../Assets/Icons/helpIcon.svg';
import stonexHeader from '../../../Assets/Icons/stonexPlusDark.svg';
import userProfilePng from '../../../Assets/Icons/userProfile.png';
import useLanguage from '../../../Services/Language/useLanguageHook';
import IconMenuDropdown from '../../Form/Inputs/IconMenuDropdown';
import styles from './ApplicationHeader.module.scss';

const ApplicationHeader: React.FC = () => {
    const [translations] = useLanguage();
    const { oktaAuth } = useOktaAuth();
    const loggedInUser = useApplicationSelector(selectUserName) ?? 'NoUserLoggedIn';

    const logoutDropdownOptions = useMemo(
        () => [
            {
                label: translations.actions.logout,
                value: 'Logout',
            },
        ],
        [translations],
    );

    const supportDropdownOptions = useMemo(
        () => [
            {
                label: <div className={styles.stand_alone_header_support}>{translations.menu.text.support}</div>,
                value: 'Support',
                isReadOnly: true,
            },
            {
                label: (
                    <div className={styles.stand_alone_header_frequently_asked_questions}>
                        <div className={styles.stand_alone_header_subheader}>{translations.menu.text.frequentlyAskedQuestionsShort}</div>
                        <a className={styles.stand_alone_header_link} href={NavigationRoutes.Faqs}>
                            {translations.menu.text.frequentlyAskedQuestions}
                        </a>
                        <a className={styles.stand_alone_header_link} href={applicationConstants.TutorialLinks.catalog}>
                            {translations.menu.text.videoGuides}
                        </a>
                    </div>
                ),
                value: 'FrequentlyAskedQuestions',
                isReadOnly: true,
            },
            {
                label: (
                    <div className={styles.stand_alone_header_support_numbers}>
                        <div className={styles.stand_alone_header_subheader}>{translations.menu.text.callSupport}</div>
                        <div className={styles.stand_alone_header_phone}>
                            <p className={styles.stand_alone_header_phone_label}>{translations.registration.text.chicagoTelephone}</p>
                            <p className={styles.stand_alone_header_phone_number}> {applicationSettings.telephoneNumbers.chicago}</p>
                        </div>
                        <div className={styles.stand_alone_header_phone}>
                            <p className={styles.stand_alone_header_phone_label}>{translations.registration.text.singaporeTelephone}</p>
                            <p className={styles.stand_alone_header_phone_number}> {applicationSettings.telephoneNumbers.singapore}</p>
                        </div>
                        <div className={styles.stand_alone_header_phone}>
                            <p className={styles.stand_alone_header_phone_label}>{translations.registration.text.dublinTelephone}</p>
                            <p className={styles.stand_alone_header_phone_number}> {applicationSettings.telephoneNumbers.dublin}</p>
                        </div>
                    </div>
                ),
                value: 'CallSupport',
                isReadOnly: true,
            },
        ],
        [translations],
    );

    const handleLogout = () => oktaAuth.signOut({ postLogoutRedirectUri: applicationSettings.okta.postLogoutRedirectUri });

    return (
        <div className={styles.logout_header}>
            <img className={styles.logout_header_image} src={stonexHeader} alt="#" />
            <div className={styles.logout_container}>
                <IconMenuDropdown
                    name="ApplicationHeaderLogout"
                    iconButton={<img className={styles.logout_image_user_profile} src={userProfilePng} alt="#" />}
                    options={logoutDropdownOptions}
                    handleSelection={handleLogout}
                    messageToDisplayAtTop={loggedInUser}
                    buttonClassName={styles.logout_image_button}
                    track={{ eventCategory: 'Application', eventAction: 'LogoutOptions' }}
                    testId="LogoutDropdown"
                />
                <IconMenuDropdown
                    name="ApplicationHeaderSupport"
                    menuStyle={{
                        paddingTop: 0,
                        paddingBottom: 0,
                    }}
                    iconButton={<img className={styles.logout_image_support} src={helpIcon} alt="#" />}
                    options={supportDropdownOptions}
                    handleSelection={handleLogout}
                    buttonClassName={styles.logout_image_button_support}
                    track={{ eventCategory: 'Application', eventAction: 'SupportOptions' }}
                />
            </div>
            <div />
        </div>
    );
};

export default ApplicationHeader;
